import { IUser } from "@/store/models/users.models";
import {
  useCreateProfileMutation,
  useGetProfilesAvatarsQuery,
} from "@/store/rtk-api/profiles.api";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  TextField,
} from "@mui/material";
import React, { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";

type Props = {
  addChildDialogOpen: boolean;
  setAddChildDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user: IUser;
  refetch: () => void;
};

export const AddChildDialog = ({
  addChildDialogOpen,
  setAddChildDialogOpen,
  user,
  refetch,
}: Props) => {
  const { data: avatars, isSuccess, isLoading } = useGetProfilesAvatarsQuery();
  const [addChild] = useCreateProfileMutation();

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      childName: "",
      childBirthday: null,
      selectedAvatar: avatars && avatars[0],
    },
  });

  const selectedAvatar = watch("selectedAvatar");

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        await addChild({
          accountInternalId: user.internal_id,
          name: data.childName,
          birthday: data.childBirthday,
          profileImage: data.selectedAvatar,
        }).unwrap();

        setAddChildDialogOpen(false);
        refetch();
        reset();
      } catch (error) {
        console.error("Error adding child:", error);
      }
    },
    [addChild, user.internal_id, setAddChildDialogOpen, refetch, reset]
  );

  const avatarStyle = useMemo(
    () => (isSelected: boolean) => ({
      width: 80,
      height: 80,
      borderRadius: "50%",
      cursor: "pointer",
      border: isSelected ? "3px solid blue" : "3px solid transparent",
    }),
    []
  );

  return (
    <Dialog
      open={addChildDialogOpen}
      onClose={() => setAddChildDialogOpen(false)}
    >
      <DialogTitle>Add Child</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <label>Choose Avatar</label>
          <Grid container spacing={2} sx={{ marginTop: 1 }}>
            {isLoading && <div>Loading...</div>}
            {isSuccess &&
              avatars.map((avatar, index) => (
                <Grid item xs={2} key={index}>
                  <img
                    src={avatar}
                    alt={`avatar-${index}`}
                    style={avatarStyle(selectedAvatar === avatar)}
                    onClick={() => setValue("selectedAvatar", avatar)}
                  />
                </Grid>
              ))}
          </Grid>
        </FormControl>
        <TextField
          fullWidth
          margin="normal"
          label="Name"
          {...register("childName", { required: "Name is required" })}
          error={!!errors.childName}
          helperText={errors.childName?.message}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Birthday"
          type="date"
          {...register("childBirthday", { required: "Birthday is required" })}
          error={!!errors.childBirthday}
          helperText={errors.childBirthday?.message}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAddChildDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleSubmit(onSubmit)} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

